import React from "react";
import { Link } from "react-router-dom";
import { AIPageShell, AIVideo, AIDiagram } from "./DualAIShared";
import ragVideo from "./assets/ai-engeeningr/rag.mp4";
import ragImage from "./assets/ai-engeeningr/rag.png";
import vectorHubImage from "./assets/ai-engeeningr/vectorHub.png";

const offlineSteps = [
  ["01", "Ingest", "Collect approved business knowledge, project information, slot structures, media metadata and operational documentation from the sources the workflow trusts."],
  ["02", "Normalize", "Clean, split and normalize content into retrieval-sized units while preserving source identifiers and logical structure."],
  ["03", "Enrich", "Attach metadata used for authorization, tenant isolation, source tracing, workflow filtering and downstream ranking."],
  ["04", "Embed and index", "Build semantic vectors and searchable indexes so lexical, semantic and metadata signals can be combined at query time."],
];

const onlineSteps = [
  ["01", "Understand the request", "Interpret the question, current project state and user objective to know what evidence scope is appropriate."],
  ["02", "Hybrid retrieve", "Combine vector similarity, lexical matching and metadata constraints to collect a candidate set of evidence."],
  ["03", "Rerank", "Reorder candidates so the strongest, most relevant chunks rise before the final context window is assembled."],
  ["04", "Build grounded context", "Create a compact evidence package with source references instead of sending the entire corpus to the model."],
  ["05", "Generate or recommend", "Ask the model to answer, summarize or propose using the retrieved evidence and current workflow instruction."],
];

const qualityControls = [
  ["Source provenance", "Keep the origin of every retrieved chunk visible so teams can trace where the answer or proposal came from."],
  ["Metadata filtering", "Filter by project, tenant, content family, document type or workflow step before a model ever sees the data."],
  ["Top-K discipline", "Constrain retrieval to the smallest useful evidence set to reduce both token waste and hallucination pressure."],
  ["Reranking", "Apply a second-pass relevance pass so near-matches do not outrank the most decision-critical evidence."],
  ["Feedback loop", "Capture user or workflow feedback to identify weak retrieval behavior and improve chunking, indexing or ranking."],
  ["Fallback behavior", "When evidence is weak or absent, avoid presenting unsupported output as grounded knowledge."],
];


const architectureDecisions = [
  ["Index-time vs runtime separation", "Keep ingestion and indexing independent from the user request path so updates do not block interactive retrieval."],
  ["Authorization before context", "Apply tenant and project filters before evidence is assembled, not after the model has already received the data."],
  ["Hybrid search", "Combine semantic similarity with lexical and metadata signals instead of relying on a single retrieval mechanism."],
  ["Reranking layer", "Use a second relevance step to improve the final context window without over-expanding the first retrieval pass."],
  ["Freshness and re-indexing", "Treat changed or deleted knowledge as an indexing lifecycle concern so stale context does not remain authoritative."],
  ["Evidence observability", "Measure retrieval hit quality, empty-result rate and source distribution to understand whether the problem sits in the corpus, the query or the ranker."],
];

const knowledgeSources = [
  ["Project context", "Project briefs, slot definitions, contributor rules and campaign state."],
  ["Media metadata", "Tags, time, place, role, source and moderation context attached to assets."],
  ["Business knowledge", "Operational guidelines, playbooks, product documentation and approved reference material."],
  ["Workflow memory", "Past decisions, reviewed outputs and contextual signals collected during the Weave lifecycle."],
];

export default function DualAIConnectionPage() {
  return (
    <AIPageShell
      pageTitle="AI Connection"
      currentPath="/dual-ai/connexion"
      eyebrow="WEAVEVO AI / AI CONNECTION"
      heroTitle="Connect the model to evidence, not guesswork."
      heroText="AI Connection is Weavevo's grounded intelligence layer. It retrieves the right evidence from project memory, business knowledge and media metadata before a model generates, recommends or explains anything."
      finalEyebrow="AI CONNECTION"
      finalTitle="Better retrieval creates better creation."
      finalText="The quality of grounded output depends on the entire information pipeline: ingestion, metadata, indexing, retrieval, reranking and disciplined context construction."
    >
      <section className="dai-section dai-ragOverview">
        <div className="dai-ragOverviewGrid">
          <div className="dai-aiHubSectionLead">
            <span className="dai-sectionEyebrow">WHY AI CONNECTION</span>
            <h2>Do not force the model to remember what the platform can retrieve in real time.</h2>
            <p>
              Retrieval-Augmented Generation separates long-lived enterprise knowledge from model weights. The result is
              a system that can stay current, scoped and auditable without retraining the model every time the business
              context changes.
            </p>
          </div>
          <AIVideo src={ragVideo} label="AI CONNECTION / RAG PIPELINE" />
        </div>
      </section>

      <section className="dai-section dai-aiUseCaseRailSection">
        <div className="dai-aiHubSectionLead dai-aiHubSectionLeadCompact">
          <span className="dai-sectionEyebrow">CONNECTED SOURCES</span>
          <h2>The model can reason across multiple enterprise context layers.</h2>
        </div>
        <div className="dai-aiUseCaseRail dai-aiUseCaseRailFour">
          {knowledgeSources.map(([title, text], index) => (
            <article key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <AIDiagram kind="rag" />

      <section className="dai-generatedGallery" aria-label="Grounding and retrieval visuals">
        <figure className="dai-generatedVisual"><img src={ragImage} alt="Grounded retrieval architecture" loading="lazy" /></figure>
        <figure className="dai-generatedVisual"><img src={vectorHubImage} alt="Vector hub and knowledge retrieval" loading="lazy" /></figure>
      </section>

      <section className="dai-ragPipelineSection">
        <div className="dai-ragPipelineInner">
          <div className="dai-aiHubSectionLead dai-aiHubSectionLeadOnDark">
            <span className="dai-sectionEyebrow">OFFLINE PIPELINE</span>
            <h2>Prepare knowledge before users ask the first question.</h2>
            <p>
              The offline path is optimized for knowledge quality, traceability and retrieval readiness. It produces the
              indexed memory layer that the runtime system depends on.
            </p>
          </div>
          <div className="dai-ragFlowGrid">
            {offlineSteps.map(([number, title, text]) => (
              <article key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dai-section dai-ragOnlineSection">
        <div className="dai-aiHubSectionLead">
          <span className="dai-sectionEyebrow">ONLINE PIPELINE</span>
          <h2>At runtime, retrieve first and generate second.</h2>
          <p>
            The online path is latency-sensitive. It scopes the request, retrieves candidate evidence, reranks that
            evidence and then builds a compact context window the model can reason over cleanly.
          </p>
        </div>
        <div className="dai-ragRuntimeRail">
          {onlineSteps.map(([number, title, text]) => (
            <article key={number}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="dai-section dai-aiEnterpriseSection">
        <div className="dai-aiHubSectionLead dai-aiHubSectionLeadCompact">
          <span className="dai-sectionEyebrow">ARCHITECTURE COMMITTEE VIEW</span>
          <h2>The retrieval layer must be secure, observable and independently evolvable.</h2>
          <p>RAG becomes enterprise-grade when indexing, authorization, search, ranking and evidence lifecycle are treated as separate architectural responsibilities.</p>
        </div>
        <div className="dai-aiCapabilityMatrix">
          {architectureDecisions.map(([title, text]) => (
            <article key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="dai-section dai-ragQualitySection">
        <div className="dai-ragQualityGrid">
          <div>
            <span className="dai-sectionEyebrow">QUALITY CONTROLS</span>
            <h2>AI Connection is an information-retrieval discipline before it is an LLM feature.</h2>
            <p>
              Weak chunking, missing metadata or noisy retrieval can degrade even a strong model. That is why retrieval
              quality, provenance and fallback behavior are first-class engineering controls.
            </p>
            <Link className="dai-aiTextLink dai-aiTextLinkDark" to="/dual-ai/generation">
              See how grounded context feeds AI Generation <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="dai-ragQualityCards">
            {qualityControls.map(([title, text]) => (
              <article key={title}>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </AIPageShell>
  );
}
